import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Inbox, TriangleAlert, type LucideIcon, Landmark, Plug, Send } from "lucide-react";
import { ErrorState, LoadingState } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCalendarDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBooks } from "./BooksProvider";
import { useLoad } from "./useLoad";
import { useBankLineSummary } from "./BankLines";
import { BooksPeriodScreen, money, num } from "./shared";
import { addDays, addMonths, lastDay, monthLabel, monthOf, monthsBetween, periodLabel, useMonthlySummary, usePeriodLink, type Period } from "./period";
import { MonthBars, platformName, SectionHeading, useDrilldown, usePlatform, type DrillTarget } from "./ReportKit";
import type { Book, CashFlowMonth, MonthlySummaryMonth, StatementGroup } from "./types";

/**
 * The Reports overview, after Mercury's Books home: each report in brief for the period, every figure a way down into
 * its report, the general ledger or the bank transactions behind it. The checks come first: a figure is only as good as
 * the transactions still to review and its agreement with the accounting platform.
 */
export function ReportsOverview() {
  return (
    <BooksPeriodScreen
      basis
      title="Reports"
      subtitle="Your books for the period at a glance. Click any figure to see the entries behind it."
    >
      {(book, period) => <Overview book={book} period={period} />}
    </BooksPeriodScreen>
  );
}

function Overview({ book, period }: { book: Book; period: Period }) {
  const { books } = useBooks();
  const drill = useDrilldown(period);
  const summary = useMonthlySummary(period.basis);
  const pl = useLoad(
    () => books.GET("/v1/books/current/profit-and-loss", { params: { query: { From: period.fromDate, To: period.toDate, Basis: period.basis } } }),
    [books, period.fromDate, period.toDate, period.basis],
  );
  const tbEnd = useLoad(
    () => books.GET("/v1/books/current/trial-balance", { params: { query: { Date: period.asAt, Basis: period.basis } } }),
    [books, period.asAt, period.basis],
  );
  // Each bank account's opening and closing balance for the period, the same figures Cash flow analysis shows.
  const flow = useLoad(
    () => books.GET("/v1/books/current/cash-flow", { params: { query: { From: period.fromDate, To: period.asAt, Basis: period.basis } } }),
    [books, period.fromDate, period.asAt, period.basis],
    // A period that ends before the books start has no cash in the books (and the endpoint refuses it).
    { enabled: period.asAt >= (book.startDate ?? "") },
  );
  const bs = useLoad(
    () => books.GET("/v1/books/current/balance-sheet", { params: { query: { Date: period.asAt, Basis: period.basis } } }),
    [books, period.asAt, period.basis],
  );

  return (
    <div className="space-y-8">
      <Checks book={book} period={period} uncategorised={uncategorisedBalance(book, tbEnd.data?.rows)} />
      <ProfitAndLossSection period={period} summary={summary} groups={pl.data?.groups} grossProfit={pl.data ? num(pl.data.grossProfit) : null} loading={pl.loading && !pl.data} error={pl.error} onRetry={pl.reload} drill={drill.open} />
      <CashSection
        book={book}
        period={period}
        months={summary.data?.months}
        flow={flow.data?.months}
        loading={flow.loading && !flow.data}
        error={flow.error}
        onRetry={flow.reload}
        drill={drill.open}
      />
      <BalanceSheetSection period={period} data={bs.data} loading={bs.loading && !bs.data} error={bs.error} onRetry={bs.reload} />
      {drill.sheet}
    </div>
  );
}

/** What sits in the Uncategorised account at the period's end, whenever it arrived; null until the balances load. */
function uncategorisedBalance(book: Book, rows: TbRow[] | undefined) {
  const id = book.controlAccounts?.uncategorisedLedgerAccountId;
  if (!id || !rows) return null;
  const r = rows.find((x) => x.ledgerAccountId === id);
  return r ? num(r.debit) - num(r.credit) : 0;
}

/* ---------- Checks ---------- */

/**
 * What stands between the figures and a reader relying on them. The platform check is Dolfin's edge: the books beside
 * the client's Xero or QuickBooks, account by account.
 */
function Checks({ book, period, uncategorised }: { book: Book; period: Period; uncategorised: number | null }) {
  const { books, links } = useBooks();
  const withPeriod = usePeriodLink();
  const lines = useBankLineSummary(period);
  const platform = usePlatform();
  const name = platformName(platform);
  const accrual = period.basis === "Accrual";
  const compared = useLoad(
    () => books.GET("/v1/books/current/trial-balance", { params: { query: { Date: period.asAt, Compare: "platform", Basis: "Accrual" } } }),
    [books, period.asAt],
    // Only once the connection is known to work, so a disconnected or unreadable platform is never asked.
    { enabled: accrual && platform?.connected === true },
  );
  const toReview = (lines.data ?? []).reduce((n, a) => n + num(a.uncategorised) + num(a.possibleDuplicate), 0);
  const failed = num(book.progress?.failed);
  const differ = (compared.data?.rows ?? []).filter((r) => num(r.difference) !== 0).length;
  // One state for the platform check; its text follows it, so it never reads as failed while still loading.
  const compareState =
    !accrual || compared.error || (compared.data && !compared.data.compared)
      ? "info"
      : compared.data
        ? differ === 0
          ? "ok"
          : "warn"
        : "pending";

  return (
    <section aria-label="Checks" className="grid gap-px overflow-hidden rounded-xl border bg-border @xl:grid-cols-2 @4xl:grid-cols-4">
      <CheckItem
        icon={Landmark}
        to={withPeriod("/books/bank-lines?status=Uncategorised")}
        title="Bank transactions"
        state={lines.data ? (toReview === 0 ? "ok" : "warn") : "pending"}
        detail={!lines.data ? "Checking" : toReview === 0 ? `All reviewed in ${period.label}` : `${toReview} to review in ${period.label}`}
      />
      <CheckItem
        icon={Inbox}
        // Every date: money parked in Uncategorised is missing from the P&L whenever it arrived.
        to={`/books/bank-lines?${new URLSearchParams({ status: "Uncategorised", from: period.months[0] ?? period.from, to: period.months[period.months.length - 1] ?? period.to })}`}
        title="Uncategorised"
        state={uncategorised === null ? "pending" : Math.abs(uncategorised) < 0.005 ? "ok" : "warn"}
        detail={
          uncategorised === null
            ? "Checking"
            : Math.abs(uncategorised) < 0.005
              ? "Nothing waiting to be categorised"
              : `${money(Math.abs(uncategorised))} waiting to be categorised, any date`
        }
      />
      <CheckItem
        icon={Send}
        to="/books/postings?status=Failed"
        title="Postings"
        state={book.progress ? (failed === 0 ? "ok" : "warn") : "pending"}
        detail={!book.progress ? "Checking" : failed === 0 ? "Every document is in the books" : `${failed} document${failed === 1 ? "" : "s"} failed to post`}
      />
      {platform === null || platform?.connected === false ? (
        // Platforms are optional, but this check is the reason to connect one.
        <CheckItem
          icon={Plug}
          to={links.accountingConnection}
          title={platform?.name ? `Against ${platform.name}` : "Accounting platform"}
          state="info"
          detail={
            platform === null
              ? "Couldn't read the accounting connection"
              : platform.name
                ? `Reconnect ${platform.name} to check these books`
                : "Connect Xero or QuickBooks to check these books"
          }
        />
      ) : (
        <CheckItem
          icon={Plug}
          to={withPeriod("/books/trial-balance")}
          title={`Against ${name}`}
          state={compareState}
          detail={
            !accrual
              ? "Compared on accrual basis only: switch to Accrual to check"
              : compareState === "pending"
                ? `Comparing with ${name}`
                : compareState === "info"
                  ? `Couldn't compare with ${name}`
                  : differ === 0
                    ? `Agrees on every account at ${formatCalendarDate(period.asAt)}`
                    : `${differ} account${differ === 1 ? "" : "s"} differ at ${formatCalendarDate(period.asAt)}`
          }
        />
      )}
    </section>
  );
}

const CHECK_TONE = {
  ok: "text-emerald-600 dark:text-emerald-400",
  warn: "text-amber-600 dark:text-amber-400",
  info: "text-muted-foreground",
  pending: "text-muted-foreground",
} as const;

function CheckItem({
  icon: Icon,
  to,
  title,
  detail,
  state,
}: {
  icon: LucideIcon;
  to: string;
  title: string;
  detail: string;
  state: keyof typeof CHECK_TONE;
}) {
  const Mark = state === "ok" ? Check : state === "warn" ? TriangleAlert : Icon;
  return (
    <Link
      to={to}
      className="flex items-start gap-3 bg-card px-4 py-3 transition hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:outline-none"
    >
      <Mark className={cn("mt-0.5 size-4 shrink-0", CHECK_TONE[state])} />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{title}</span>
        <span className={cn("block text-xs", state === "warn" ? "text-foreground" : "text-muted-foreground")}>{detail}</span>
      </span>
    </Link>
  );
}

/* ---------- Profit and loss ---------- */

type Summary = ReturnType<typeof useMonthlySummary>;

function ProfitAndLossSection({
  period,
  summary,
  groups,
  grossProfit,
  loading,
  error,
  onRetry,
  drill,
}: {
  period: Period;
  summary: Summary;
  groups?: StatementGroup[];
  grossProfit: number | null;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  drill: (targets: DrillTarget[], at: number) => void;
}) {
  const withPeriod = usePeriodLink();
  const navigate = useNavigate();
  const all = summary.data?.months ?? [];
  const byMonth = new Map(all.map((m) => [(m.month ?? "").slice(0, 7), m]));
  const months = monthsBetween(period.from, period.to);
  const pick = (key: "income" | "expenses" | "net") => months.map((m) => num(byMonth.get(m)?.[key]));
  const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
  const income = sum(pick("income"));
  const expenses = sum(pick("expenses"));
  const net = income - expenses;
  // The same number of months straight before, when the books cover them.
  const prior = monthsBetween(addMonths(period.from, -months.length), addMonths(period.from, -1));
  const covered = prior.every((m) => byMonth.has(m));
  const priorOf = (key: "income" | "expenses" | "net") => (covered ? sum(prior.map((m) => num(byMonth.get(m)?.[key]))) : null);
  const changeOf = (now: number, before: number | null) => (before !== null && before !== 0 ? (now - before) / Math.abs(before) : null);
  const change = changeOf(net, priorOf("net"));

  const gross = grossProfit ?? null;
  // Gross margin is on sales: the Revenue section, not other income.
  const sales = (groups ?? []).flatMap((g) => g.rows ?? []).filter((r) => r.section === "Revenue").reduce((t, r) => t + num(r.amount), 0);
  const revenue = groups?.find((g) => g.classification === "revenue");
  const expense = groups?.find((g) => g.classification === "expense");
  const caption = periodLabel(period.from, period.to);
  const targets = (g: StatementGroup | undefined, natural: DrillTarget["natural"]) =>
    sorted(g).map((r) => ({
      accountId: r.ledgerAccountId!,
      label: r.code ? `${r.code} · ${r.name}` : (r.name ?? ""),
      from: period.fromDate,
      to: period.toDate,
      amount: r.amount,
      natural,
      caption,
    }));

  return (
    <section className="space-y-4">
      <SectionHeading title="Profit and loss" to={withPeriod("/books/profit-and-loss?view=monthly")} cta="Profit and loss" />
      {summary.error ? (
        <ErrorState message={apiErrorMessage(summary.error, "Couldn't load the monthly figures.")} onRetry={summary.reload} />
      ) : (
        <div className="grid gap-6 @3xl:grid-cols-[minmax(0,15rem)_1fr]">
          <div className="space-y-3">
            <Figure label="Net profit" value={summary.data ? money(net) : "-"} tone={net < 0 ? "down" : undefined}>
              {change !== null ? (
                <span className={cn("text-xs font-medium tabular-nums", change >= 0 ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400")}>
                  {change >= 0 ? "+" : ""}
                  {Math.round(change * 100)}% vs {periodLabel(prior[0]!, prior[prior.length - 1]!)}
                </span>
              ) : null}
            </Figure>
            <dl className="grid gap-x-6 gap-y-2 text-sm @xl:grid-cols-2 @3xl:grid-cols-1">
              <MiniFigure label="Income" value={summary.data ? money(income) : "-"} change={changeOf(income, priorOf("income"))} />
              <MiniFigure label="Expenses" value={summary.data ? money(expenses) : "-"} change={changeOf(expenses, priorOf("expenses"))} />
              <MiniFigure label="Gross margin" value={gross !== null && sales !== 0 ? `${Math.round((gross / sales) * 100)}%` : "-"} />
              <MiniFigure label="Net margin" value={summary.data && income !== 0 ? `${Math.round((net / income) * 100)}%` : "-"} />
            </dl>
          </div>
          <MonthBars
            labels={months.map((m) => monthLabel(m))}
            series={[
              { label: "Income", className: "bg-emerald-500/70", values: pick("income") },
              { label: "Expenses", className: "bg-rose-500/60", values: pick("expenses") },
            ]}
            summary={caption}
            openLast={period.asAt < period.toDate}
            onPick={(i) => navigate(`/books/profit-and-loss?${new URLSearchParams({ from: months[i]!, to: months[i]!, basis: period.basis })}`)}
          />
        </div>
      )}
      {loading ? (
        <LoadingState label="Loading accounts" />
      ) : error ? (
        <ErrorState message={apiErrorMessage(error, "Couldn't load the profit and loss.")} onRetry={onRetry} />
      ) : (
        <div className="grid gap-6 @3xl:grid-cols-2">
          <ShareList
            title="Income by account"
            group={revenue}
            barClass="bg-emerald-500/60"
            more={withPeriod("/books/profit-and-loss")}
            onOpen={(i) => drill(targets(revenue, "credit"), i)}
          />
          <ShareList
            title="Expenses by account"
            group={expense}
            barClass="bg-rose-500/50"
            more={withPeriod("/books/profit-and-loss")}
            onOpen={(i) => drill(targets(expense, "debit"), i)}
          />
        </div>
      )}
    </section>
  );
}

const sorted = (g: StatementGroup | undefined) =>
  (g?.rows ?? []).filter((r) => r.ledgerAccountId && num(r.amount) !== 0).sort((a, b) => num(b.amount) - num(a.amount));

const SHARE_ROWS = 8;

/** An account breakdown: each account's share of the group's total, largest first. */
function ShareList({
  title,
  group,
  barClass,
  more,
  onOpen,
}: {
  title: string;
  group?: StatementGroup;
  barClass: string;
  more: string;
  onOpen: (index: number) => void;
}) {
  const rows = sorted(group);
  const total = num(group?.total);
  const shown = rows.slice(0, SHARE_ROWS);
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between text-xs">
        <h3 className="font-medium text-muted-foreground">{title}</h3>
        <span className="tabular-nums text-muted-foreground">{money(total)}</span>
      </div>
      {shown.length === 0 ? (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">Nothing in this period</p>
      ) : (
        <ol className="divide-y rounded-lg border">
          {shown.map((r, i) => {
            const share = total > 0 ? num(r.amount) / total : 0;
            return (
              <li key={r.ledgerAccountId}>
                <button
                  type="button"
                  onClick={() => onOpen(i)}
                  className="grid w-full grid-cols-[1fr_7rem_2.75rem] items-center gap-x-3 px-3 py-2 text-left text-sm transition hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                >
                  <span className="min-w-0">
                    <span className="block truncate">{r.name}</span>
                    <span className="mt-1 block h-1 overflow-hidden rounded-full bg-muted">
                      <span className={cn("block h-full rounded-full", barClass)} style={{ width: `${Math.max(0, Math.min(1, share)) * 100}%` }} />
                    </span>
                  </span>
                  <span className="text-right tabular-nums">{money(r.amount)}</span>
                  <span className="text-right text-xs tabular-nums text-muted-foreground">{share > 0 && share < 0.01 ? "<1%" : `${Math.round(share * 100)}%`}</span>
                </button>
              </li>
            );
          })}
        </ol>
      )}
      {rows.length > SHARE_ROWS ? (
        <Link to={more} className="block text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline">
          {rows.length - SHARE_ROWS} more in the profit and loss
        </Link>
      ) : null}
    </div>
  );
}

/* ---------- Cash ---------- */

type TbRow = { ledgerAccountId?: string | null; debit?: number | string; credit?: number | string };

function CashSection({
  book,
  period,
  months,
  flow,
  loading,
  error,
  onRetry,
  drill,
}: {
  book: Book;
  period: Period;
  months?: MonthlySummaryMonth[];
  flow?: CashFlowMonth[];
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  drill: (targets: DrillTarget[], at: number) => void;
}) {
  const withPeriod = usePeriodLink();
  const first = flow?.[0];
  const last = flow?.[flow.length - 1];
  const balance = (m: CashFlowMonth | undefined, id: string, key: "opening" | "closing") =>
    num(m?.accounts?.find((x) => x.ledgerAccountId === id)?.[key]);
  const accounts = (book.bankAccounts ?? []).map((a) => ({
    ...a,
    opening: balance(first, a.ledgerAccountId!, "opening"),
    closing: balance(last, a.ledgerAccountId!, "closing"),
  }));
  const opening = accounts.reduce((s, a) => s + a.opening, 0);
  const closing = accounts.reduce((s, a) => s + a.closing, 0);
  const movement = closing - opening;
  const caption = periodLabel(period.from, period.to);
  // Coverage, after Mercury: how many months of spending the cash would cover, at the last three full months' rate.
  // Full months only: the as-at month counts once it is over.
  const done = (m: string) => m < monthOf(period.asAt) || (m === monthOf(period.asAt) && period.asAt === lastDay(m));
  const full = (months ?? []).filter((m) => done((m.month ?? "").slice(0, 7))).slice(-3);
  const burn = full.length === 3 ? full.reduce((s, m) => s + num(m.expenses), 0) / 3 : 0;
  const coverage = burn > 0 && closing > 0 ? closing / burn : null;
  const targets: DrillTarget[] = accounts.map((a) => ({
    accountId: a.ledgerAccountId!,
    bankAccountId: a.bankAccountId ?? undefined,
    label: a.code ? `${a.code} · ${a.name}` : (a.name ?? ""),
    // From the endpoint's first day (never before the books), so the lines add up to the movement shown.
    from: first?.from ?? period.fromDate,
    to: last?.to ?? period.asAt,
    amount: a.closing - a.opening,
    natural: "debit",
    caption: `Movement in ${caption}`,
  }));

  return (
    <section className="space-y-4">
      <SectionHeading title="Cash" to={withPeriod("/books/cash-flow")} cta="Cash flow analysis">
        <InfoHint label="About cash">
          The bank and card accounts in the books. Net cash flow is the change from the opening balance to the period's end.
        </InfoHint>
      </SectionHeading>
      {loading || (!flow && !error && period.asAt >= (book.startDate ?? "")) ? (
        <LoadingState label="Loading bank balances" />
      ) : error ? (
        <ErrorState message={apiErrorMessage(error, "Couldn't load the bank balances.")} onRetry={onRetry} />
      ) : period.asAt < (book.startDate ?? "") ? (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
          The books start on {formatCalendarDate(book.startDate ?? "")}, after this period.
        </p>
      ) : accounts.length === 0 ? (
        <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">No bank accounts in the books</p>
      ) : (
        <div className="grid gap-6 @3xl:grid-cols-[minmax(0,15rem)_1fr]">
          <div className="space-y-3">
            <Figure label={`Cash at ${formatCalendarDate(period.asAt)}`} value={money(closing)} />
            <dl className="grid gap-x-6 gap-y-2 text-sm @xl:grid-cols-3 @3xl:grid-cols-1">
              {/* The endpoint starts no earlier than the books, so the opening date comes from its first month. */}
              <MiniFigure label={`Opening, ${formatCalendarDate(addDays(first?.from ?? period.fromDate, -1))}`} value={money(opening)} />
              <MiniFigure label="Net cash flow" value={`${movement > 0 ? "+" : ""}${money(movement)}`} tone={movement < 0 ? "down" : "up"} />
              <MiniFigure
                label="Coverage"
                hint="Cash at the end of the period divided by the average monthly expenses of the last three full months."
                value={coverage === null ? "-" : coverage >= 24 ? "24+ months" : `${coverage.toFixed(1)} months`}
              />
            </dl>
          </div>
          <ol className="divide-y self-start rounded-lg border">
            {accounts.map((a, i) => {
              const move = a.closing - a.opening;
              return (
                <li key={a.ledgerAccountId} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => drill(targets, i)}
                    className="grid min-w-0 flex-1 grid-cols-[1fr_auto_auto] items-baseline gap-x-4 px-3 py-2 text-left text-sm transition hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
                  >
                    <span className="min-w-0 truncate">{a.code ? `${a.code} · ${a.name}` : a.name}</span>
                    <span className={cn("text-xs tabular-nums", move < 0 ? "text-rose-700 dark:text-rose-400" : move > 0 ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground")}>
                      {move > 0 ? "+" : ""}
                      {money(move)}
                    </span>
                    <span className="w-28 text-right tabular-nums">{money(a.closing)}</span>
                  </button>
                  {a.bankAccountId ? (
                    <Link
                      to={withPeriod(`/books/bank-lines?account=${a.bankAccountId}`)}
                      className="shrink-0 px-3 py-2 text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      Transactions
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </section>
  );
}

/* ---------- Balance sheet ---------- */

function BalanceSheetSection({
  period,
  data,
  loading,
  error,
  onRetry,
}: {
  period: Period;
  data?: { groups?: StatementGroup[]; balanced?: boolean; totalAssets?: number | string; workingCapital?: number | string };
  loading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const withPeriod = usePeriodLink();
  const total = (c: string) => num(data?.groups?.find((g) => g.classification === c)?.total);
  return (
    <section className="space-y-4">
      <SectionHeading title="Balance sheet" to={withPeriod("/books/balance-sheet")} cta="Balance sheet">
        {data ? (
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium",
              data.balanced ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" : "bg-destructive/10 text-destructive",
            )}
          >
            {data.balanced ? <Check className="size-3" /> : <TriangleAlert className="size-3" />}
            {data.balanced ? "Balances" : "Does not balance"}
          </span>
        ) : null}
      </SectionHeading>
      {loading ? (
        <LoadingState label="Loading the balance sheet" />
      ) : error ? (
        <ErrorState message={apiErrorMessage(error, "Couldn't load the balance sheet.")} onRetry={onRetry} />
      ) : (
        <dl className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border bg-border @xl:grid-cols-2 @3xl:grid-cols-4">
          {(
            [
              ["Assets", total("asset")],
              ["Liabilities", total("liability")],
              ["Equity", total("equity")],
              // Current assets less current liabilities.
              ["Working capital", num(data?.workingCapital)],
            ] as const
          ).map(([label, value]) => (
            <Link key={label} to={withPeriod("/books/balance-sheet")} className="bg-card px-4 py-3 transition hover:bg-muted/40">
              <dt className="text-xs text-muted-foreground">
                {label} at {formatCalendarDate(period.asAt)}
              </dt>
              <dd className="mt-0.5 text-lg font-semibold tabular-nums">{money(value)}</dd>
            </Link>
          ))}
        </dl>
      )}
    </section>
  );
}

/* ---------- Figures ---------- */

function Figure({ label, value, tone, children }: { label: string; value: string; tone?: "down"; children?: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn("text-3xl font-semibold tracking-tight tabular-nums", tone === "down" && "text-rose-700 dark:text-rose-400")}>{value}</p>
      {children}
    </div>
  );
}

function MiniFigure({
  label,
  value,
  tone,
  hint,
  change,
}: {
  label: string;
  value: string;
  tone?: "up" | "down";
  hint?: string;
  /** Against the same number of months before the period; neutral, since more expenses is not always worse. */
  change?: number | null;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-dashed pb-1.5 last:border-b-0">
      <dt className="flex items-center gap-1 text-muted-foreground">
        {label}
        {hint ? <InfoHint label={label}>{hint}</InfoHint> : null}
      </dt>
      <dd
        className={cn(
          "font-medium tabular-nums",
          tone === "down" && "text-rose-700 dark:text-rose-400",
          tone === "up" && "text-emerald-700 dark:text-emerald-400",
        )}
      >
        {change != null ? (
          <span className="mr-2 text-xs font-normal text-muted-foreground">
            {change >= 0 ? "+" : ""}
            {Math.round(change * 100)}%
          </span>
        ) : null}
        {value}
      </dd>
    </div>
  );
}
